import React, { useState, useEffect, useCallback } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';

// Icons
import { Plus, Search, Building2, Eye, Edit, Trash2, Mail, Phone, MapPin, ArrowUpDown } from 'lucide-react';

interface Cliente {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  documento?: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  tipo_cliente_id: string;
  observacoes?: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  codigo_cliente?: string;
}

interface TipoCliente {
  id: string;
  nome: string;
  status: string;
}

const INITIAL_CLIENTE = {
  nome: '',
  email: '',
  telefone: '',
  documento: '',
  endereco: '',
  cidade: '',
  estado: '',
  cep: '',
  tipo_cliente_id: '',
  observacoes: '',
  status: 'ativo'
};

export default function Clientes() {
  // Hooks
  const { hasPermission } = usePermissions();
  const { toast } = useToast();

  // Estado
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tipoClientes, setTipoClientes] = useState<TipoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [sortBy, setSortBy] = useState('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newCliente, setNewCliente] = useState(INITIAL_CLIENTE);

  // Função para carregar tipos de cliente
  const loadTipoClientes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_clientes')
        .select('id, nome, status')
        .eq('status', 'ativo')
        .order('nome');

      if (error) throw error;
      setTipoClientes(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar tipos de cliente:', error);
    }
  }, []);

  // Função para carregar clientes
  const loadClientes = useCallback(async () => {
    if (!hasPermission('clientes', 'clientes', 'read')) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      let query = supabase
        .from('clientes')
        .select('*');

      // Aplicar filtros
      if (tipoFilter !== 'all') {
        query = query.eq('tipo_cliente_id', tipoFilter);
      }

      if (search.trim()) {
        query = query.or(`nome.ilike.%${search}%,documento.ilike.%${search}%,email.ilike.%${search}%`);
      }

      // Aplicar ordenação
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;

      if (error) throw error;

      // Adicionar códigos de cliente se não existirem
      const clientesComCodigo = (data || []).map((cliente, index) => ({
        ...cliente,
        codigo_cliente: cliente.codigo_cliente || `CLI${String(index + 1).padStart(4, '0')}`
      }));

      setClientes(clientesComCodigo);
    } catch (error: any) {
      console.error('Erro ao carregar clientes:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar clientes.",
        variant: "destructive",
      });
      setClientes([]);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, tipoFilter, search, sortBy, sortOrder, toast]);

  // Carregar dados iniciais
  useEffect(() => {
    loadTipoClientes();
  }, [loadTipoClientes]);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  // Função para criar cliente
  const handleCreateCliente = async () => {
    try {
      if (!newCliente.nome.trim()) {
        toast({
          title: "Erro",
          description: "O nome do cliente é obrigatório.",
          variant: "destructive",
        });
        return;
      }

      if (!newCliente.tipo_cliente_id) {
        toast({
          title: "Erro",
          description: "O tipo de cliente é obrigatório.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('clientes')
        .insert({
          nome: newCliente.nome.trim(),
          email: newCliente.email.trim() || null,
          telefone: newCliente.telefone.trim() || null,
          cpf_cnpj: newCliente.documento.trim() || null,
          endereco: newCliente.endereco.trim() || null,
          cidade: newCliente.cidade.trim() || null,
          estado: newCliente.estado.trim() || null,
          cep: newCliente.cep.trim() || null,
          tipo_cliente_id: newCliente.tipo_cliente_id,
          observacoes: newCliente.observacoes.trim() || null,
          status: 'ativo' as const,
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Cliente "${newCliente.nome}" criado com sucesso.`,
      });

      setNewCliente(INITIAL_CLIENTE);
      setIsCreateModalOpen(false);
      loadClientes();
    } catch (error: any) {
      console.error('Erro ao criar cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o cliente.",
        variant: "destructive",
      });
    }
  };

  // Função para excluir cliente
  const handleDeleteCliente = async (id: string, nome: string) => {
    try {
      const { error } = await supabase
        .from('clientes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Cliente "${nome}" excluído com sucesso.`,
      });

      loadClientes();
    } catch (error: any) {
      console.error('Erro ao excluir cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o cliente.",
        variant: "destructive",
      });
    }
  };

  // Função para ordenação
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Ícone de ordenação
  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return <ArrowUpDown className={`w-4 h-4 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />;
  };

  // Filtrar clientes exibidos
  const filteredClientes = clientes;

  // Função para obter nome do tipo de cliente
  const getTipoClienteNome = (tipoId: string) => {
    const tipo = tipoClientes.find(t => t.id === tipoId);
    return tipo?.nome || 'N/A';
  };

  // Verificar permissões
  if (!hasPermission('clientes', 'clientes', 'read')) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Você não tem permissão para acessar os clientes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestão de Clientes</h1>
        {hasPermission('clientes', 'clientes', 'write') && (
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Criar Novo Cliente</DialogTitle>
              </DialogHeader>
              <ScrollArea className="max-h-96 pr-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome *</Label>
                      <Input
                        id="nome"
                        placeholder="Nome do cliente"
                        value={newCliente.nome}
                        onChange={(e) => setNewCliente({ ...newCliente, nome: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tipo_cliente">Tipo de Cliente *</Label>
                      <Select
                        value={newCliente.tipo_cliente_id}
                        onValueChange={(value) => setNewCliente({ ...newCliente, tipo_cliente_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {tipoClientes.map((tipo) => (
                            <SelectItem key={tipo.id} value={tipo.id}>
                              {tipo.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="email@exemplo.com"
                        value={newCliente.email}
                        onChange={(e) => setNewCliente({ ...newCliente, email: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        placeholder="(11) 99999-9999"
                        value={newCliente.telefone}
                        onChange={(e) => setNewCliente({ ...newCliente, telefone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="documento">CPF/CNPJ</Label>
                      <Input
                        id="documento"
                        placeholder="000.000.000-00"
                        value={newCliente.documento}
                        onChange={(e) => setNewCliente({ ...newCliente, documento: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cep">CEP</Label>
                      <Input
                        id="cep"
                        placeholder="00000-000"
                        value={newCliente.cep}
                        onChange={(e) => setNewCliente({ ...newCliente, cep: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endereco">Endereço</Label>
                    <Input
                      id="endereco"
                      placeholder="Rua, número, complemento"
                      value={newCliente.endereco}
                      onChange={(e) => setNewCliente({ ...newCliente, endereco: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cidade">Cidade</Label>
                      <Input
                        id="cidade"
                        placeholder="São Paulo"
                        value={newCliente.cidade}
                        onChange={(e) => setNewCliente({ ...newCliente, cidade: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="estado">Estado</Label>
                      <Input
                        id="estado"
                        placeholder="SP"
                        maxLength={2}
                        value={newCliente.estado}
                        onChange={(e) => setNewCliente({ ...newCliente, estado: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="observacoes">Observações</Label>
                    <Textarea
                      id="observacoes"
                      placeholder="Observações sobre o cliente"
                      value={newCliente.observacoes}
                      onChange={(e) => setNewCliente({ ...newCliente, observacoes: e.target.value })}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="ativo"
                      checked={newCliente.status === 'ativo'}
                      onCheckedChange={(checked) => setNewCliente({ ...newCliente, status: checked ? 'ativo' : 'inativo' })}
                    />
                    <Label htmlFor="ativo">Cliente Ativo</Label>
                  </div>
                </div>
              </ScrollArea>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setNewCliente(INITIAL_CLIENTE);
                    setIsCreateModalOpen(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreateCliente}>
                  Criar Cliente
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Buscar por nome, documento ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tipo-filter">Tipo de Cliente</Label>
              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {tipoClientes.map((tipo) => (
                    <SelectItem key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearch('');
                  setTipoFilter('all');
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Clientes */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Clientes ({filteredClientes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando clientes...</div>
          ) : filteredClientes.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum cliente encontrado.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('nome')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Nome</span>
                        {getSortIcon('nome')}
                      </div>
                    </TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Contato</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Status</span>
                        {getSortIcon('status')}
                      </div>
                    </TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClientes.map((cliente) => (
                    <TableRow key={cliente.id}>
                      <TableCell className="font-medium">{cliente.nome}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getTipoClienteNome(cliente.tipo_cliente_id)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {cliente.email && (
                            <div className="flex items-center text-sm">
                              <Mail className="w-3 h-3 mr-1" />
                              {cliente.email}
                            </div>
                          )}
                          {cliente.telefone && (
                            <div className="flex items-center text-sm">
                              <Phone className="w-3 h-3 mr-1" />
                              {cliente.telefone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{cliente.documento || '-'}</TableCell>
                      <TableCell>
                        {cliente.cidade && cliente.estado ? (
                          <div className="flex items-center text-sm">
                            <MapPin className="w-3 h-3 mr-1" />
                            {cliente.cidade}, {cliente.estado}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={cliente.status === 'ativo' ? 'default' : 'secondary'}>
                          {cliente.status === 'ativo' ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="max-w-2xl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Detalhes do Cliente</AlertDialogTitle>
                              </AlertDialogHeader>
                              <ScrollArea className="max-h-96">
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="font-semibold mb-2">Informações Básicas</h4>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium">Nome:</span>
                                          <p className="text-muted-foreground">{cliente.nome}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Tipo:</span>
                                          <p className="text-muted-foreground">{getTipoClienteNome(cliente.tipo_cliente_id)}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Documento:</span>
                                          <p className="text-muted-foreground">{cliente.documento || 'Não informado'}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Status:</span>
                                          <p className="text-muted-foreground">
                                           <Badge variant={cliente.status === 'ativo' ? 'default' : 'secondary'}>
                                             {cliente.status === 'ativo' ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="font-semibold mb-2">Contato</h4>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium">E-mail:</span>
                                          <p className="text-muted-foreground">{cliente.email || 'Não informado'}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Telefone:</span>
                                          <p className="text-muted-foreground">{cliente.telefone || 'Não informado'}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">CEP:</span>
                                          <p className="text-muted-foreground">{cliente.cep || 'Não informado'}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <h4 className="font-semibold mb-2">Endereço</h4>
                                    <div className="text-sm text-muted-foreground">
                                      {cliente.endereco && <p>{cliente.endereco}</p>}
                                      {(cliente.cidade || cliente.estado) && (
                                        <p>{cliente.cidade}{cliente.cidade && cliente.estado && ', '}{cliente.estado}</p>
                                      )}
                                      {!cliente.endereco && !cliente.cidade && !cliente.estado && (
                                        <p>Endereço não informado</p>
                                      )}
                                    </div>
                                  </div>

                                  {cliente.observacoes && (
                                    <div>
                                      <h4 className="font-semibold mb-2">Observações</h4>
                                      <p className="text-sm text-muted-foreground">{cliente.observacoes}</p>
                                    </div>
                                  )}

                                  <div>
                                    <h4 className="font-semibold mb-2">Auditoria</h4>
                                    <div className="space-y-1 text-sm text-muted-foreground">
                                      <p>Criado em: {format(new Date(cliente.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}</p>
                                      <p>Atualizado em: {format(new Date(cliente.updated_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}</p>
                                    </div>
                                  </div>
                                </div>
                              </ScrollArea>
                              <AlertDialogFooter>
                                <AlertDialogAction>Fechar</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          
                          {hasPermission('clientes', 'clientes', 'write') && (
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}

                          {hasPermission('clientes', 'clientes', 'admin') && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir Cliente</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja excluir o cliente "{cliente.nome}"? Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteCliente(cliente.id, cliente.nome)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}